import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import {
  TipoCita,
  ProgramacionServicio,
  AgendaDisponibilidad,
  ProgramacionServicioRequest,
  CancelarCitaSPRequest
} from 'src/app/domain/model/agenda/agenda';
import { ConfiguracionCita } from 'src/app/domain/model/agenda/configuracion-cita';
import { Profesional, AgendaProfesionalPageResponse } from 'src/app/domain/model/profesional/profesional';

/**
 * Adaptador HTTP: HttpAgendaRepository
 *
 * Implementa el puerto AgendaRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (AgendaController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto AgendaRepository del dominio
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpAgendaRepository implements AgendaRepository {

  private readonly http = inject(HttpClient);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly baseUrl = `${environment.backend.baseUrl}/agenda`;

  /**
   * Obtiene los headers con el DNI del usuario autenticado.
   * El backend requiere X-User-DNI para operaciones de escritura.
   */
  private getHeadersWithUserDni(dniUsuario: string): HttpHeaders {
    return new HttpHeaders({ 'X-User-DNI': dniUsuario });
  }



  // === Calendario/Disponibilidad ===

  async consultarCalendarioMes(
    asesorId: number,
    anio: number,
    mes: number
  ): Promise<AgendaDisponibilidad[]> {
    const params = new HttpParams()
      .set('anio', anio.toString())
      .set('mes', mes.toString());

    return firstValueFrom(
      this.http.get<AgendaDisponibilidad[]>(
        `${this.baseUrl}/calendario/${asesorId}`,
        { params }
      )
    );
  }

  async consultarCitasDia(
    asesorId: number,
    fecha: string
  ): Promise<ProgramacionServicio[]> {
    const params = new HttpParams().set('fecha', fecha);

    return firstValueFrom(
      this.http.get<ProgramacionServicio[]>(
        `${this.baseUrl}/citas/${asesorId}`,
        { params }
      )
    );
  }

  // === CRUD Citas ===

  async obtenerCita(citaId: number): Promise<ProgramacionServicio> {
    return firstValueFrom(
      this.http.get<ProgramacionServicio>(`${this.baseUrl}/citas/detalle/${citaId}`)
    );
  }

  async crearCita(cita: ProgramacionServicioRequest): Promise<ProgramacionServicio> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    const headers = this.getHeadersWithUserDni(dniUsuario);
    return firstValueFrom(
      this.http.post<ProgramacionServicio>(`${this.baseUrl}/citas`, cita, { headers })
    );
  }

  async actualizarCita(
    citaId: number,
    cita: ProgramacionServicioRequest
  ): Promise<ProgramacionServicio> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    const headers = this.getHeadersWithUserDni(dniUsuario);
    return firstValueFrom(
      this.http.put<ProgramacionServicio>(`${this.baseUrl}/citas/${citaId}`, cita, { headers })
    );
  }

  async cancelarCita(citaId: number, motivo?: string): Promise<ProgramacionServicio> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    const headers = this.getHeadersWithUserDni(dniUsuario);
    const params = motivo ? new HttpParams().set('motivo', motivo) : undefined;

    return firstValueFrom(
      this.http.delete<ProgramacionServicio>(`${this.baseUrl}/citas/${citaId}`, { headers, params })
    );
  }

  /**
   * Cancela una cita usando el SP del legacy con todos los parámetros.
   *
   * POST /api/agenda/sp/citas/{citaId}/cancelar
   *
   * Tipos de Cancelador:
   * - ASE: Asesor
   * - PRO: Programador
   * - BEN: Empresa (requiere nombreContacto)
   * - BES: En Sitio (requiere nombreContacto y justificación)
   * - INT: Interno (requiere nombreContacto)
   */
  async cancelarCitaSP(request: CancelarCitaSPRequest): Promise<void> {
    const dniUsuario = await this.usuarioState.obtenerDni();
    const headers = this.getHeadersWithUserDni(dniUsuario);

    // Construir el body para el endpoint
    const body = {
      codigoTipoCancelador: request.codigoTipoCancelador,
      descripcionTipoCancelador: request.descripcionTipoCancelador,
      nombreContacto: request.nombreContacto,
      observaciones: request.observaciones,
      consecutivoUsuario: request.consecutivoUsuario,
      consecutivoOrden: request.consecutivoOrden,
      cancelarFuturas: request.cancelarFuturas || 'N',
      codigoJustificacion: request.codigoJustificacion,
      descripcionJustificacion: request.descripcionJustificacion,
      motivoCancelacion: request.motivoCancelacion
    };
 

    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/sp/citas/${request.citaId}/cancelar`, body, { headers })
    );
  }

  // === Tipos de Cita ===

  /**
   * Lista los tipos de cita activos.
   *
   * Sigue el legacy (CrearCitaAdministrativaViewImpl.java línea 2056-2058):
   * Los tipos se filtran por TPSC_TIPO_CITA_EMPLEADO donde NMCONSEC_EMPLEADO corresponde al empleado.
   *
   * @param dniEmpleado DNI del empleado (ej: "C43480070")
   * @param usoCita 'ADMIN' para administrativas, 'SERVICIO' para Campo/Informe (default: 'ADMIN')
   */
  async listarTiposCita(dniEmpleado?: string, usoCita: 'ADMIN' | 'SERVICIO' = 'ADMIN'): Promise<TipoCita[]> {
    const params: string[] = [];
    if (dniEmpleado) {
      params.push(`dniEmpleado=${encodeURIComponent(dniEmpleado)}`);
    }
    if (usoCita !== 'ADMIN') {
      params.push(`usoCita=${usoCita}`);
    }
    const url = params.length > 0
      ? `${this.baseUrl}/tipos-cita?${params.join('&')}`
      : `${this.baseUrl}/tipos-cita`;
    return firstValueFrom(this.http.get<TipoCita[]>(url));
  }

  async obtenerTipoCita(tipoCitaId: number): Promise<TipoCita> {
    return firstValueFrom(
      this.http.get<TipoCita>(`${this.baseUrl}/tipos-cita/${tipoCitaId}`)
    );
  }

  async consultarCitasRango(
    asesorId: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]> {
    const params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);

    return firstValueFrom(
      this.http.get<ProgramacionServicio[]>(
        `${this.baseUrl}/citas/${asesorId}/rango`,
        { params }
      )
    );
  }

  /**
   * Consulta citas por CDPERSONA del asesor (de TPSC_PERSONAS).
   * Usa el endpoint que hace JOIN con TPSC_EMPLEADOS para resolver
   * el nmconsecutivo_asesor correcto.
   */
  async consultarCitasRangoByCdpersona(
    cdpersona: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]> {
    const params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);

    return firstValueFrom(
      this.http.get<ProgramacionServicio[]>(
        `${this.baseUrl}/citas/persona/${cdpersona}/rango`,
        { params }
      )
    );
  }

  /**
   * Consulta citas por DNI del asesor (de TPSC_PERSONAS).
   * Sigue el patrón del legacy (SP_DIS_CONSULTAR_CITAS_AGENDA con ivadniprestador).
   * Este es el método recomendado ya que replica el comportamiento exacto del sistema legacy.
   */
  async consultarCitasRangoByDni(
    dni: string,
    fechaInicio: string,
    fechaFin: string
  ): Promise<ProgramacionServicio[]> {
    const params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);

    return firstValueFrom(
      this.http.get<ProgramacionServicio[]>(
        `${this.baseUrl}/citas/dni/${dni}/rango`,
        { params }
      )
    );
  }

  /**
   * Consulta disponibilidad por CDPERSONA del asesor
   */
  async consultarDisponibilidadByCdpersona(
    cdpersona: number,
    fechaInicio: string,
    fechaFin: string
  ): Promise<AgendaDisponibilidad[]> {
    const params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);

    return firstValueFrom(
      this.http.get<AgendaDisponibilidad[]>(
        `${this.baseUrl}/disponibilidad/persona/${cdpersona}`,
        { params }
      )
    );
  }

  // === Búsqueda ===

  async buscarAsesoresDisponibles(
    fecha: string,
    duracionHoras: number,
    municipio?: string
  ): Promise<number[]> {
    let params = new HttpParams()
      .set('fecha', fecha)
      .set('duracionHoras', duracionHoras.toString());

    if (municipio) {
      params = params.set('municipio', municipio);
    }

    return firstValueFrom(
      this.http.get<number[]>(`${this.baseUrl}/asesores-disponibles`, { params })
    );
  }

  // === Profesionales (PPR) ===

  /**
   * Busca profesionales por DNI y/o nombre.
   * Legacy: BusquedaActivity.java con TABLE_NAME_TPSC_PROFESIONALES
   * Endpoint: GET /api/agenda/profesionales/buscar?dni=X&nombre=Y
   *
   * Usado para tipo de cita SEGUIMIENTO_INTEGRAL_CLIENTE (25)
   *
   * @param dni DNI parcial del profesional (opcional)
   * @param nombre Nombre parcial del profesional (opcional)
   */
  async buscarProfesionales(dni?: string, nombre?: string): Promise<Profesional[]> {
    let params = new HttpParams();

    if (dni) {
      params = params.set('dni', dni);
    }
    if (nombre) {
      params = params.set('nombre', nombre);
    }

    return firstValueFrom(
      this.http.get<Profesional[]>(`${this.baseUrl}/profesionales/buscar`, { params })
    );
  }

  /**
   * Busca profesionales con paginación.
   * Sigue el patrón de BeneficiarioRepository.buscarPaginado()
   *
   * Endpoint: GET /api/agenda/profesionales/buscar-paginado?dni=X&nombre=Y&page=0&size=10
   *
   * Usado para tipo de cita SEGUIMIENTO_INTEGRAL_CLIENTE (25)
   *
   * @param dni DNI parcial del profesional (opcional)
   * @param nombre Nombre parcial del profesional (opcional)
   * @param page Número de página (0-based), default 0
   * @param size Tamaño de página, default 10
   */
  async buscarProfesionalesPaginado(
    dni?: string,
    nombre?: string,
    page = 0,
    size = 10
  ): Promise<AgendaProfesionalPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (dni) {
      params = params.set('dni', dni);
    }
    if (nombre) {
      params = params.set('nombre', nombre);
    }

    return firstValueFrom(
      this.http.get<AgendaProfesionalPageResponse>(`${this.baseUrl}/profesionales/buscar-paginado`, { params })
    );
  }

  // === Configuración ===

  /**
   * Obtiene la configuración de citas del sistema.
   *
   * GET /api/agenda/configuracion
   *
   * Legacy: GeneralParameters, Constants.java
   * - CITA_MINIMA_DURACION (default: 0)
   * - CITA_MAXIMA_DURACION (default: 24)
   * - HOLIDAYS (lista de festivos)
   */
  async obtenerConfiguracion(): Promise<ConfiguracionCita> {
    return firstValueFrom(
      this.http.get<ConfiguracionCita>(`${this.baseUrl}/configuracion`)
    );
  }
}
